import { Ajax2 } from '@shared/Ajax';
import { ComparisonResponse, ComparisonResponseSchema, ComparisonApiParams, SaveVolumeApiParams, MergeVolumeApiParams, StockpileResponseSchema, StockpileResponse, demApiParams, VariationApiParams } from '../../../../../typings/reality-capture/volumes/models';

//const isDev = window.location.hostname === 'localhost'
const isLocal = false

// Determine API URL
let realityCaptureBaseUrl: string
let timeout: number
let aiBaseUrl: string
if (isLocal) {
    realityCaptureBaseUrl = 'http://localhost:8000/reality-capture/v1'
    aiBaseUrl = 'http://localhost:8000/reality-capture/v1'
    timeout = 240000
} else {
    aiBaseUrl = kuura.AwsApiGatewayDomain() + "/ai-api-gpu/v1"
    realityCaptureBaseUrl = kuura.AwsApiGatewayDomain() + "/reality-capture/v1"
    timeout = 30000
    console.log('kuura.getServerDomain()', kuura.getServerDomain())
    if (kuura.getServerDomain().includes('beta')) {
        realityCaptureBaseUrl = "https://api-beta.infrakit.com/reality-capture/v1"
    }
    else {
        realityCaptureBaseUrl = kuura.AwsApiGatewayDomain() + "/reality-capture/v1"
    }
}

export interface PresignedUrlResponse {
    presigned_url: string;
    s3_key: string;
}

export async function presignedUrlApi(extension: string): Promise<PresignedUrlResponse> {
    const apiUrl = `${aiBaseUrl}/presigned-url`;
    const data = {
        project_uuid: kuura.getProjectUuid(),
        extension: extension
    };
    const response = await Ajax2({
        url: apiUrl,
        data: data,
        contentType: 'json',
        method: 'POST',
        useToken: true,
        timeout: timeout,
    })
    return response
}

export async function segmentApi(geotiffKey: string, geojsonKey: string, bounds: number[]): Promise<Blob> {
    const apiUrl = `${aiBaseUrl}/raster/segment/bbox`
    const data = {
        project_uuid: kuura.getProjectUuid(),
        geotiff_s3_key: geotiffKey,
        geojson_s3_key: geojsonKey,
        bounds: bounds
    };
    const response = await Ajax2({
        url: apiUrl,
        data: data,
        method: 'POST',
        useToken: true,
        contentType: 'json',
        responseType: 'blob',
        timeout: timeout,
    })
    return response
}

export async function segmentTextApi(geotiffKey: string, bounds: number[], text: string): Promise<Blob> {
    const apiUrl = `${aiBaseUrl}/raster/segment/text`
    const data = {
        project_uuid: kuura.getProjectUuid(),
        geotiff_s3_key: geotiffKey,
        bounds: bounds,
        text: text
    };
    const response = await Ajax2({
        url: apiUrl,
        data: data,
        method: 'POST',
        useToken: true,
        contentType: 'json',
        responseType: 'blob',
        timeout: timeout,
    })
    return response
}

export async function segmentImageApi(geotiffKey: string, geojsonKey: string, bounds: number[]): Promise<Blob> {
    const apiUrl = `${aiBaseUrl}/raster/segment/img`
    const data = {
        project_uuid: kuura.getProjectUuid(),
        geotiff_s3_key: geotiffKey,
        geojson_s3_key: geojsonKey,
        bounds: bounds
    };
    const response = await Ajax2({
        url: apiUrl,
        data: data,
        method: 'POST',
        useToken: true,
        contentType: 'json',
        responseType: 'blob',
        timeout: timeout,
    })
    return response
}


export async function stockpileApi(params: demApiParams): Promise<StockpileResponse> {
    const apiUrl = `${realityCaptureBaseUrl}/vol-calc/stockpile`
    const formData = stockpileForm(params);
    const response = await Ajax2({
        url: apiUrl,
        data: formData,
        method: 'POST',
        useToken: true,
        contentType: 'multipart/form-data',
        timeout: timeout,
    })
    return StockpileResponseSchema.parse(response)
}

export async function comparisonApi(params: ComparisonApiParams): Promise<ComparisonResponse> {
    const apiUrl = `${realityCaptureBaseUrl}/vol-calc/comparison`
    const formData = comparisonForm(params);
    const response = await Ajax2({
        url: apiUrl,
        data: formData,
        method: 'POST',
        useToken: true,
        contentType: 'multipart/form-data',
        timeout: timeout,
    })
    return ComparisonResponseSchema.parse(response)
}

export async function VariationApi(params: VariationApiParams) {
    console.log('params', params)
    const apiUrl = `${realityCaptureBaseUrl}/vol-calc/variation`
    const formData = VariationForm(params);
    return Ajax2({
        url: apiUrl,
        data: formData,
        method: 'POST',
        useToken: true,
        contentType: 'multipart/form-data',
        timeout: timeout,
    })
}

export async function mergeVolumeApi(params: MergeVolumeApiParams) {
    const apiUrl = `${realityCaptureBaseUrl}/vol-calc/merge`
    return Ajax2({
        url: apiUrl,
        data: params,
        method: 'POST',
        useToken: true,
        contentType: 'json',
        timeout: timeout,
    })
}

export async function saveVolumeApi(params: SaveVolumeApiParams) {
    const apiUrl = `${realityCaptureBaseUrl}/vol-crud/save`
    const formData = addVolumeForm(params);
    return Ajax2({
        url: apiUrl,
        data: formData,
        method: 'POST',
        useToken: true,
        contentType: 'multipart/form-data',
        timeout: timeout,
    })
}

export const getVolumesByProject = async(project_uuid) => {
    const apiUrl = `${realityCaptureBaseUrl}/vol-crud/project/${project_uuid}`
    return Ajax2({
        url: apiUrl,
        method: 'GET',
        useToken: true,
    })
}

export async function deleteVolumeApi(volume_uuid) {
    const apiUrl = `${realityCaptureBaseUrl}/vol-crud/proj/${kuura.getProjectUuid()}/vol/${volume_uuid}` 
    return Ajax2({
        url: apiUrl,
        method: 'DELETE',
        useToken: true,
        timeout: timeout,
    })
}

function stockpileForm(params: demApiParams) {
    const { tolerance, area, kml_segment, dem_url, customBaseHeight } = params;
    const formData = new FormData();
    const formFields = {
        'project_uuid': kuura.getProjectUuid(),
        'area': area.toString(),
        'dem_s3_key': dem_url,
        'tolerance': tolerance.toString(),
        'file': kml_segment,
    };
    Object.entries(formFields).forEach(([key, value]) => formData.append(key, value));

    if (customBaseHeight) {
        formData.append('base_height', customBaseHeight.toString());
    }

    return formData;
}

function comparisonForm(params: ComparisonApiParams) {
    const { cacheUuid, area, kml_segment, tolerance, target, reference, variation } = params;
    console.log(variation)
    const formData = new FormData();
    const formFields = {
        'project_uuid': kuura.getProjectUuid(),
        'cache_uuid': cacheUuid,
        'area': area.toString(),
        'from_type': target.surfaceType,
        'from_model_uuid': target.surfaceModelUuid,
        'from_s3_key': target.surfaceDemUrl,
        'to_type': reference.surfaceType,
        'to_model_uuid': reference.surfaceModelUuid,
        'to_s3_key': reference.surfaceDemUrl,
        'tolerance': tolerance.toString(),
        'color': variation.color.toString(),
        'min_z': variation.min_z.toString(),
        'max_z': variation.max_z.toString(),
        'file': kml_segment,
    };
    Object.entries(formFields).forEach(([key, value]) => formData.append(key, value));
    return formData;
}

function VariationForm(params: VariationApiParams) {
    const { reference, target, file, tolerance } = params;
    const formData = new FormData();
    const formFields = {
        'project_uuid': kuura.getProjectUuid(),
        'from_type': target.surfaceType,
        'from_model_uuid': target.surfaceModelUuid,
        'from_s3_key': target.surfaceDemUrl,
        'to_type': reference.surfaceType,
        'to_model_uuid': reference.surfaceModelUuid,
        'to_s3_key': reference.surfaceDemUrl,
        'file': file,
        'tolerance': tolerance.toString(),
    };
    Object.entries(formFields).forEach(([key, value]) => formData.append(key, value));
    return formData;
}

function addVolumeForm(params: SaveVolumeApiParams) {
    const { folderUuid, fill_volume, cut_volume, baseHeight, tolerance, area, file, target, reference, highest_point, lowest_point, failure_rate, colors, type, name, desc } = params;
    const formData = new FormData();
    const formFields = {
        'type': type,
        'name': name,
        'desc': desc,
        'project_uuid': kuura.getProjectUuid(),
        'folder_uuid': folderUuid,
        'area': area.toString(),
        'fill_volume': fill_volume.toString(),
        'cut_volume': cut_volume.toString(),
        ...(baseHeight
            ? { 'base_height': baseHeight.toString() }
            : {
                'to_type': target.surfaceType,
                'to_name': target.text,
                'to_model_uuid': target.surfaceModelUuid,
                'to_s3_key': target.surfaceDemUrl,
            }
        ),
        'tolerance': tolerance.toString(),
        'kml_file': file,
        'from_type': reference.surfaceType,
        'from_name': reference.text,
        'from_model_uuid': reference.surfaceModelUuid,
        'from_s3_key': reference.surfaceDemUrl,
        'failure_rate': failure_rate.toString(),
        'highest_point': highest_point.toString(),
        'lowest_point': lowest_point.toString(),
        'color': colors.toString(),
        'dem_url': params.dem_url,
        'heatmap_url': params.heatmap_url,
        'alpha_url': params.alpha_url,
        'bounds': params.bounds,
    };
    Object.entries(formFields).forEach(([key, value]) => formData.append(key, value));
    return formData;
}
